import apiClient from '../../../../services/apiClient';

/**
 * Review API Service
 * Handles all API calls for the Creator Review System
 */
class ReviewApiService {
  /**
   * Start a new review session - loads all creators at once
   * @param {string[]} creatorIds - Array of creator IDs to review
   * @param {Object} options - Options for future use
   * @returns {Promise<Object>} Session object with ID and initial state
   */
  async startReviewSession(creatorIds, options = {}) {
    try {
      const response = await apiClient.post('/api/reviews/session', {
        creatorIds
      });
      return response.data.data || response.data; // Handle nested response structure
    } catch (error) {
      console.error('Error starting review session:', error);
      throw error;
    }
  }

  /**
   * Get review session state
   * @param {string} sessionId - Session ID
   * @returns {Promise<Object>} Session state
   */
  async getSession(sessionId) {
    try {
      const response = await apiClient.get(`/api/reviews/session/${sessionId}`);
      return response.data.data || response.data; // Handle nested response structure
    } catch (error) {
      console.error('Error getting session:', error);
      throw error;
    }
  }

  /**
   * Update review session
   * @param {string} sessionId - Session ID
   * @param {Object} sessionData - Updated session data
   * @returns {Promise<Object>} Updated session
   */
  async updateSession(sessionId, sessionData) {
    try {
      const response = await apiClient.put(`/api/reviews/session/${sessionId}`, sessionData);
      return response.data.data || response.data; // Handle nested response structure
    } catch (error) {
      console.error('Error updating session:', error);
      throw error;
    }
  }

  /**
   * Create or update a creator review
   * @param {Object} reviewData - Review data object
   * @returns {Promise<Object>} Saved review
   */
  async saveReview(reviewData) {
    try {
      const response = await apiClient.post('/api/reviews', reviewData);
      return response.data.data || response.data; // Handle nested response structure
    } catch (error) {
      console.error('Error saving review:', error);
      throw error;
    }
  }

  /**
   * Get all reviews for a specific creator
   * @param {string} creatorId - Creator ID
   * @returns {Promise<Object[]>} Array of reviews
   */
  async getCreatorReviews(creatorId) {
    try {
      const response = await apiClient.get(`/api/reviews/creator/${creatorId}`);
      return response.data.data || response.data; // Handle nested response structure
    } catch (error) {
      console.error('Error getting creator reviews:', error);
      throw error;
    }
  }

  /**
   * Get creator's campaign posts with engagement metrics and pagination
   * @param {string} creatorId - Creator ID
   * @param {Object} options - Query options (limit, skip, includeTwitterContent)
   * @returns {Promise<Object>} Response with posts and pagination info
   */
  async getCreatorCampaignPosts(creatorId, options = {}) {
    try {
      const { limit = 20, skip = 0, includeTwitterContent = true } = options;
      const params = new URLSearchParams({
        limit: limit.toString(),
        skip: skip.toString(),
        includeTwitterContent: includeTwitterContent.toString()
      });

      const response = await apiClient.get(`/creators/${creatorId}/campaign-posts?${params}`);
      return response.data.data || response.data; // Handle nested response structure
    } catch (error) {
      console.error('Error getting creator campaign posts:', error);
      throw error;
    }
  }

  /**
   * Get all available status options
   * @returns {Promise<string[]>} Array of status options
   */
  async getStatusOptions() {
    try {
      const response = await apiClient.get('/creators/status-options');
      return response.data.data || response.data; // Handle nested response structure
    } catch (error) {
      console.error('Error getting status options:', error);
      throw error;
    }
  }

  /**
   * Add a new status option
   * @param {string} status - New status value
   * @returns {Promise<Object>} Created status option
   */
  async addStatusOption(status) {
    try {
      const response = await apiClient.post('/creators/status-options', {
        status
      });
      return response.data.data || response.data; // Handle nested response structure
    } catch (error) {
      console.error('Error adding status option:', error);
      throw error;
    }
  }

  /**
   * Get recent tweets for multiple creators - DEPRECATED
   * Twitter sync functionality has been removed from review system
   * @param {string[]} handles - Array of Twitter handles
   * @param {Object} options - Options (limit, forceRefresh)
   * @returns {Promise<Object>} Empty object (Twitter sync disabled)
   */
  async getCreatorsRecentTweets(handles, options = {}) {
    // Twitter sync functionality has been removed
    console.log('getCreatorsRecentTweets called but Twitter sync is disabled');
    return {};
  }

  /**
   * Load more creators for a session (lazy loading)
   * @param {string} sessionId - Session ID
   * @param {Object} options - Options (batchSize)
   * @returns {Promise<Object>} Additional creators
   */
  async loadMoreCreators(sessionId, options = {}) {
    try {
      const { batchSize = 10, offset = 0 } = options;
      const response = await apiClient.post(`/api/reviews/session/${sessionId}/load-more`, {
        batchSize,
        offset
      });
      return response.data.data || response.data;
    } catch (error) {
      console.error('Error loading more creators:', error);
      throw error;
    }
  }

  /**
   * Get performance metrics for the review system
   * @param {string} period - Time period (1h, 1d, 1w)
   * @returns {Promise<Object>} Performance metrics
   */
  async getPerformanceMetrics(period = '1h') {
    try {
      const response = await apiClient.get(`/api/reviews/metrics?period=${period}`);
      return response.data.data || response.data;
    } catch (error) {
      console.error('Error getting performance metrics:', error);
      throw error;
    }
  }
}

// Export singleton instance
export const reviewApiService = new ReviewApiService();
export default reviewApiService;